/**
 * Vehicle Service - Frontend API calls for vehicle operations
 */

import api from "../../../shared/api.jsx";

/**
 * Get vehicles with optional filters
 * @param {Object} filters - Filter criteria
 * @param {string} filters.vehicleType - Vehicle type (small, medium, large)
 * @param {string} filters.ownerType - Owner type (bubakii, third_party)
 * @param {string} filters.partnerId - Partner ID
 * @param {string} filters.status - Status (available, in_use, maintenance, unavailable)
 * @returns {Promise<Object>} Vehicles list
 */
export const getVehicles = async (filters = {}) => {
  try {
    const response = await api.get("/vehicles", { params: filters });
    // API client returns the JSON response directly
    // Backend returns { success: true, data: [...] }
    if (response && response.success !== undefined) {
      return response;
    }
    // Fallback if response format is different
    return {
      success: true,
      data: response || [],
    };
  } catch (error) {
    console.error("Error fetching vehicles:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch vehicles",
    };
  }
};

/**
 * Get vehicle by ID
 * @param {string} vehicleId - Vehicle ID
 * @returns {Promise<Object>} Vehicle details
 */
export const getVehicle = async (vehicleId) => {
  try {
    const response = await api.get(`/vehicles/${vehicleId}`);
    // API client returns the JSON response directly
    if (response && response.success !== undefined) {
      return response;
    }
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    console.error("Error fetching vehicle:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch vehicle",
    };
  }
};

/**
 * Create vehicle
 * @param {Object} vehicleData - Vehicle data
 * @param {string} vehicleData.licensePlate - License plate
 * @param {string} vehicleData.make - Make
 * @param {string} vehicleData.model - Model
 * @param {number} vehicleData.year - Year
 * @param {string} vehicleData.vehicleType - Vehicle type (small, medium, large)
 * @param {number} vehicleData.capacity - Capacity in kg
 * @param {string} vehicleData.ownerType - Owner type (bubakii, third_party)
 * @param {string} [vehicleData.partnerId] - Partner ID if third_party
 * @returns {Promise<Object>} Creation result
 */
export const createVehicle = async (vehicleData) => {
  try {
    const response = await api.post("/vehicles", vehicleData);
    // API client returns the JSON response directly
    if (response && response.success !== undefined) {
      return response;
    }
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    console.error("Error creating vehicle:", error);
    return {
      success: false,
      error: error.message || "Failed to create vehicle",
    };
  }
};

/**
 * Update vehicle
 * @param {string} vehicleId - Vehicle ID
 * @param {Object} updates - Update data
 * @returns {Promise<Object>} Update result
 */
export const updateVehicle = async (vehicleId, updates) => {
  try {
    const response = await api.put(`/vehicles/${vehicleId}`, updates);
    // API client returns the JSON response directly
    if (response && response.success !== undefined) {
      return response;
    }
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    console.error("Error updating vehicle:", error);
    return {
      success: false,
      error: error.message || "Failed to update vehicle",
    };
  }
};

/**
 * Get available vehicles
 * @param {Date|string} date - Date to check availability (optional)
 * @param {string} vehicleType - Vehicle type filter
 * @param {Object} filters - Additional filters
 * @returns {Promise<Object>} Available vehicles
 */
export const getAvailableVehicles = async (date = null, vehicleType = null, filters = {}) => {
  try {
    const params = { ...filters };
    if (date) {
      params.date = date instanceof Date ? date.toISOString().split("T")[0] : date;
    }
    if (vehicleType) {
      params.vehicleType = vehicleType;
    }
    const response = await api.get("/vehicles/available", { params });
    // API client returns the JSON response directly
    if (response && response.success !== undefined) {
      return response;
    }
    return {
      success: true,
      data: response || [],
    };
  } catch (error) {
    console.error("Error fetching available vehicles:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch available vehicles",
    };
  }
};

/**
 * Assign driver to vehicle
 * @param {string} vehicleId - Vehicle ID
 * @param {string} driverId - Driver ID
 * @returns {Promise<Object>} Assignment result
 */
export const assignDriver = async (vehicleId, driverId) => {
  try {
    const response = await api.post(`/vehicles/${vehicleId}/assign-driver`, { driverId });
    // API client returns the JSON response directly
    if (response && response.success !== undefined) {
      return response;
    }
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    console.error("Error assigning driver to vehicle:", error);
    return {
      success: false,
      error: error.message || "Failed to assign driver to vehicle",
    };
  }
};

/**
 * Unassign driver from vehicle
 * @param {string} vehicleId - Vehicle ID
 * @returns {Promise<Object>} Unassignment result
 */
export const unassignDriver = async (vehicleId) => {
  try {
    const response = await api.post(`/vehicles/${vehicleId}/unassign-driver`);
    // API client returns the JSON response directly
    if (response && response.success !== undefined) {
      return response;
    }
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    console.error("Error unassigning driver from vehicle:", error);
    return {
      success: false,
      error: error.message || "Failed to unassign driver from vehicle",
    };
  }
};

